import ImageUploading, { ImageListType } from "react-images-uploading";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { ReactComponent as UploadIcon } from "shared/assets/Icons/upload.svg";

type ChooseImageModalProps = {
  onAdd: (file: File) => Promise<void>;
  onCancel: VoidFunction;
};

const maxFileSizeMb = 10;
const maxFileSizeBytes = 1000000 * maxFileSizeMb;

export const ChooseImageModal = ({ onAdd, onCancel }: ChooseImageModalProps) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<ImageListType>([]);
  const [showImageRequiredError, setShowImageRequiredError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadError, setShowUploadError] = useState(false);

  const handleImagesChange = (imageList: ImageListType) => {
    setImages(imageList);
    setShowImageRequiredError(false);
    setShowUploadError(false);
  };

  const handleAdd = async () => {
    if (images.length === 0) {
      setShowImageRequiredError(true);
      return;
    }

    setIsUploading(true);
    setShowUploadError(false);
    try {
      await onAdd(images[0].file!);
    } catch {
      setShowUploadError(true);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ConfirmationModal
      title={t("hubSites.chooseImageModal.title")}
      confirmButtonText={isUploading ? t("hubSites.chooseImageModal.uploadingButton") : t("hubSites.chooseImageModal.addButton")}
      cancelButtonText={t("hubSites.chooseImageModal.cancelButton")}
      confirmButtonDisabled={isUploading}
      cancelButtonDisabled={isUploading}
      onConfirm={handleAdd}
      onCancel={onCancel}
    >
      <div data-testid="choose-image-container">
        <ImageUploading value={images} onChange={handleImagesChange} maxNumber={1} maxFileSize={maxFileSizeBytes} acceptType={["png", "jpg", "jpeg"]}>
          {({ imageList, onImageUpload, isDragging, dragProps, errors }) => (
            <div>
              {errors?.maxFileSize && <ErrorAlert content={t("hubSites.chooseImageModal.errors.maxFileSize", { limit: maxFileSizeMb })} />}
              {imageList.length === 0 ? (
                <button
                  className={classNames("p-8 w-full rounded-lg border-dashed border-2 transition-all", {
                    "border-brand-red": showImageRequiredError,
                    "border-sky-grey hover:border-black": !showImageRequiredError,
                  })}
                  style={isDragging ? { borderColor: "black", backgroundColor: "#E5E5E5" } : undefined}
                  onClick={onImageUpload}
                  data-testid="upload-button"
                  {...dragProps}
                >
                  <div className="flex justify-center align-middle flex-col pointer-events-none">
                    <UploadIcon className="w-full mb-6 h-[4em]" />
                    <div className="mb-4">{t("hubSites.chooseImageModal.dropZoneText")}</div>
                    <div className="text-l-grey">{t("hubSites.chooseImageModal.dragZoneFileLimitations", { limit: maxFileSizeMb })}</div>
                  </div>
                </button>
              ) : (
                <img
                  className="w-100 m-auto max-h-[18rem] rounded-lg"
                  src={imageList[0].dataURL}
                  alt={t("hubSites.chooseImageModal.previewAlt")}
                />
              )}
            </div>
          )}
        </ImageUploading>
        {showImageRequiredError && <ErrorAlert content={t("hubSites.chooseImageModal.imageRequiredError")} />}
        {showUploadError && <ErrorAlert content={t("hubSites.chooseImageModal.uploadError")} />}
      </div>
    </ConfirmationModal>
  );
};
